import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";
import { formatBuildingLabel } from "../utils/apartmentDisplay";

const { KEYS: COOKIE_KEYS } = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.35,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

// apartmentCode → 아파트(양수인) 이름 매핑
const APARTMENT_NAME_MAP = {
  Richmond: "더리치먼드평창",
  Raycounty: "레이카운티",
  Harringtonplace: "해링턴플레이스GTX운정",
  Singilpark: "신길파크자이",
  DMCArtForest: "DMC아트포레자이",
  GodeokUrbanBridge: "e편한세상고덕어반브릿지",
  GeomdanUrbanCenter: "e편한세상검단어반센트로",
  GeumgangPenteriumSeaglo: "금강펜테리움더시글로",
  GeumgangPenteriumSeagloOffice: "아라역금강펜테리움더시글로1차오피스텔",
  GangbyeonStarhills: "덕소강변스타힐스",
  YangjuOkjeongLH: "양주옥정LH엘리프",
  UlsanGwellCityXiComplex1: "울산지웰시티자이1단지",
  YeongheungForestPrugioParkBien: "영흥숲푸르지오파크비엔",
  HillstateYonginGojinStationComplex1: "힐스테이트용인고진역1단지",
  HillstateYonginGojinStationComplex2: "힐스테이트용인고진역2단지",
  HillstateBongdamPrideCity: "힐스테이트봉담프라이드시티",
  PyeongchonIrvineFirstTheSharp: "평촌어바인퍼스트더샵",
  NaepoNewTownMoaMiraedMegacity2nd: "내포신도시모아미래도메가시티2차",
  BucheonIllumiState: "부천일루미스테이트",
  SongdoStationSeohaeGrandbleThePark: "송도역서해그랑블더파크",
  Acrobestinu: "아크로베스티뉴",
  BupyeongHowstoryUrbanGreen: "부평하우스토리어반그린",
  ForeonStation5: "포레온스테이션5",
  HyundaiTerraTowerGuriGalmae: "현대테라타워구리갈매",
  테스트: "테스트아파트",
};

// 생년월일(YYYYMMDD) → YYYY.MM.DD
const formatBirthDate = (value) => {
  if (!value) return "-";
  const v = String(value).replace(/[^\d]/g, "");
  if (v.length !== 8) return value;
  return `${v.slice(0, 4)}.${v.slice(4, 6)}.${v.slice(6, 8)}`;
};

// 연락처(01012345678) → 010-1234-5678
const formatPhone = (value) => {
  if (!value) return "-";
  const v = String(value).replace(/[^\d]/g, "");
  if (v.length === 11) return `${v.slice(0, 3)}-${v.slice(3, 7)}-${v.slice(7)}`;
  if (v.length === 10) return `${v.slice(0, 3)}-${v.slice(3, 6)}-${v.slice(6)}`;
  return value;
};

function SubmissionConfirmPage() {
  const navigate = useNavigate();
  const topRef = useRef(null);

  const [info, setInfo] = useState({});
  const [capturedImage, setCapturedImage] = useState(null);
  const [coOwners, setCoOwners] = useState([]);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useLayoutEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "auto", block: "start" });
    }
  }, []);

  useEffect(() => {
    // 신분증 이미지 로드
    let image = null;
    try {
      image = localStorage.getItem("temp_captured_image");
    } catch (err) {}

    if (!image) {
      // 촬영된 신분증이 없으면 제출 페이지로 되돌림
      navigate("/id-card-submit", { replace: true });
      return;
    }
    setCapturedImage(image);

    // 입력 정보 로드
    try {
      const cookieData = Cookies.get("buildingUnitInfo");
      if (cookieData) {
        const parsed = JSON.parse(cookieData);
        setInfo(parsed);
        setCoOwners(parsed.coOwners || []);
      }
    } catch (err) {}
  }, [navigate]);

  const apartmentCode = Cookies.get("apartmentCode") || "";
  const apartmentName = APARTMENT_NAME_MAP[apartmentCode] || "";
  const assigneeOrgLabel =
    apartmentCode === "ForeonStation5" ? "관리단" : "입주자대표회의";

  // 신분증 사진 수정 (제출 페이지에서 다시 촬영/선택)
  const handleEditPhoto = () => {
    navigate("/id-card-submit");
  };

  const getPresignedUrls = async (files) => {
    const response = await axios.get(
      `${API_ENDPOINTS.BASE_URL}/mobile/file/upload`,
      {
        params: {
          type: "image",
          mimeType: "image/png",
          imageUploadTarget: "claimIdCardImage",
          num: files.length,
        },
        headers: {
          Authorization: `Bearer ${Cookies.get(COOKIE_KEYS.ACCESS_TOKEN)}`,
        },
      }
    );
    return response.data;
  };

  // 확인 → 신분증 제출까지 진행 후 완료 페이지로 이동
  const handleConfirm = async () => {
    if (!capturedImage) {
      navigate("/id-card-submit");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);

      // 1. Base64 이미지를 File 객체로 변환
      const imageFile = await (async () => {
        const response = await fetch(capturedImage);
        const blob = await response.blob();
        return new File([blob], `idcard_${Date.now()}.png`, {
          type: "image/png",
        });
      })();

      // 2. Presigned URL 가져오기
      setUploadProgress(20);
      const presignedUrlData = await getPresignedUrls([imageFile]);

      if (!presignedUrlData || !presignedUrlData[0]?.url) {
        throw new Error("Presigned URL을 가져오는데 실패했습니다.");
      }

      setUploadProgress(40);

      // 3. S3에 업로드
      const presignedUrl = presignedUrlData[0].url;
      const s3Response = await fetch(presignedUrl, {
        method: "PUT",
        body: imageFile,
        headers: {
          "Content-Type": "image/png",
        },
      });

      if (!s3Response.ok) {
        throw new Error(
          `S3 업로드 실패: ${s3Response.status} ${s3Response.statusText}`
        );
      }

      setUploadProgress(80);

      // S3 URL 추출 및 이미지 경로만 사용
      const originalUrl = presignedUrl.split("?")[0];

      const date = new Date();
      const yearMonth = `${date.getFullYear()}${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;

      const fileName = originalUrl.split("/").pop();

      if (!fileName) {
        throw new Error("업로드된 파일 경로가 유효하지 않습니다.");
      }

      const imagePath = `images/origin/claimIdCardImage/${yearMonth}/${fileName}`;

      setUploadProgress(90);

      // 4. 신분증 이미지 URL을 서버에 전송
      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim/claimIdCardImage`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            claimIdCardImage: imagePath,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("신분증 제출에 실패했습니다.");
      }

      setUploadProgress(95);

      // 5. /claim/check API 호출 (실패해도 진행)
      try {
        await axios.get(`${API_ENDPOINTS.BASE_URL}/mobile/claim/check`);
      } catch (checkError) {
        console.error("Claim Check API 호출 실패:", checkError);
      }

      // 5-1. 서버에 신분증이 실제로 저장되었는지 검증
      const verifyResponse = await axios.get(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!verifyResponse.data || verifyResponse.data.claimIdCard !== true) {
        throw new Error("신분증 저장이 확인되지 않았습니다. 다시 시도해주세요.");
      }

      // 6. 공동소유자 정보가 있으면 POST /mobile/claim/share API 호출
      const buildingUnitInfo = Cookies.get("buildingUnitInfo");
      if (buildingUnitInfo) {
        try {
          const parsedBuildingUnitInfo = JSON.parse(buildingUnitInfo);
          const owners = parsedBuildingUnitInfo.coOwners || [];

          if (owners.length > 0) {
            for (const coOwner of owners) {
              try {
                await axios.post(
                  `${API_ENDPOINTS.BASE_URL}/mobile/claim/share`,
                  {
                    shareName: coOwner.name,
                    sharePhone: coOwner.phone,
                    shareBirth: coOwner.birthDate,
                  },
                  {
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                      "Content-Type": "application/json",
                    },
                  }
                );
              } catch (shareError) {
                console.error(
                  `공동소유자 ${coOwner.name} API 호출 실패:`,
                  shareError
                );
              }
            }
          }
        } catch (parseError) {
          console.error("공동소유자 정보 파싱 실패:", parseError);
        }
      }

      // 신분증 저장이 검증된 경우에만 완료 페이지로 이동
      setUploadProgress(100);
      setTimeout(() => {
        navigate("/completion");
      }, 500);
    } catch (error) {
      setUploadError("신분증을 재촬영후 다시 제출해주세요.");
      setShowErrorModal(true);
      setTimeout(() => {
        setShowErrorModal(false);
      }, 3000);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <motion.div
      ref={topRef}
      style={{ padding: "25px" }}
      className="page-container"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <motion.div
        className="page-header"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35 }}
        style={{ marginTop: "0", marginBottom: "20px" }}
      >
        <h1>제출 내용 확인</h1>
      </motion.div>

      {/* 입력 정보 요약 */}
      <div className="contract-summary" style={{ marginBottom: "20px" }}>
        <h3 style={{ textAlign: "left" }}>신청 정보</h3>
        {apartmentName && (
          <div className="summary-row">
            <div className="summary-label">양수인</div>
            <div className="summary-value">
              {apartmentName} {assigneeOrgLabel}
            </div>
          </div>
        )}
        <div className="summary-row">
          <div className="summary-label">동·호수</div>
          <div className="summary-value">
            {formatBuildingLabel(info.building || "-", apartmentCode)}{" "}
            {info.unit || "-"}호
          </div>
        </div>
        <div className="summary-row">
          <div className="summary-label">소유자 성명</div>
          <div className="summary-value">{info.applicantName || "-"}</div>
        </div>
        <div className="summary-row">
          <div className="summary-label">생년월일</div>
          <div className="summary-value">
            {formatBirthDate(info.applicantBirthDate)}
          </div>
        </div>
        <div className="summary-row">
          <div className="summary-label">연락처</div>
          <div className="summary-value">
            {formatPhone(info.applicantPhone)}
          </div>
        </div>
      </div>

      {/* 공동소유자 정보 */}
      {coOwners.length > 0 && (
        <div className="contract-summary" style={{ marginBottom: "20px" }}>
          <h3 style={{ textAlign: "left" }}>공동소유자 정보</h3>
          {coOwners.map((coOwner, index) => (
            <div
              key={index}
              style={{
                paddingBottom: index < coOwners.length - 1 ? "8px" : "0",
                marginBottom: index < coOwners.length - 1 ? "8px" : "0",
                borderBottom:
                  index < coOwners.length - 1 ? "1px solid #e0e0e0" : "none",
              }}
            >
              <div className="summary-row" style={{ borderBottom: "none" }}>
                <div className="summary-label">
                  성명{coOwner.relation ? ` (${coOwner.relation})` : ""}
                </div>
                <div className="summary-value">{coOwner.name}</div>
              </div>
              <div className="summary-row" style={{ borderBottom: "none" }}>
                <div className="summary-label">생년월일</div>
                <div className="summary-value">
                  {formatBirthDate(coOwner.birthDate)}
                </div>
              </div>
              <div className="summary-row" style={{ borderBottom: "none" }}>
                <div className="summary-label">연락처</div>
                <div className="summary-value">
                  {formatPhone(coOwner.phone)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 신분증 사진 */}
      <div className="contract-summary" style={{ marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "14px",
          }}
        >
          <h3 style={{ margin: 0, textAlign: "left" }}>신분증 사진</h3>
          <button
            onClick={handleEditPhoto}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              background: "none",
              border: "none",
              color: "#7A4FFF",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              padding: "4px",
              WebkitTapHighlightColor: "rgba(0,0,0,0)",
            }}
          >
            <img
              src="/reset.png"
              alt="수정"
              style={{ width: "16px", height: "16px", pointerEvents: "none" }}
            />
            사진 수정
          </button>
        </div>
        {capturedImage && (
          <img
            src={capturedImage}
            alt="촬영된 신분증"
            style={{
              width: "100%",
              height: "auto",
              aspectRatio: "325/208",
              objectFit: "contain",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)",
            }}
          />
        )}
      </div>

      {/* 하단 버튼 */}
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          marginTop: "auto",
          marginBottom: "20px",
        }}
      >
        <button
          onClick={handleEditPhoto}
          style={{
            backgroundColor: "#EBEBFF",
            color: "#7A4FFF",
            height: "56px",
            width: "100%",
            maxWidth: "460px",
            borderRadius: "8px",
            border: "none",
            fontSize: "16px",
            fontWeight: 700,
            margin: "0 auto",
            cursor: "pointer",
            WebkitTapHighlightColor: "rgba(0,0,0,0)",
            WebkitAppearance: "none",
          }}
        >
          신분증 사진 수정
        </button>
        <button
          onClick={handleConfirm}
          disabled={isUploading}
          style={{
            backgroundColor: "#7A4FFF",
            color: "#FFFFFF",
            height: "56px",
            width: "100%",
            maxWidth: "460px",
            borderRadius: "8px",
            border: "none",
            fontSize: "16px",
            fontWeight: 700,
            margin: "0 auto",
            cursor: isUploading ? "default" : "pointer",
            opacity: isUploading ? 0.7 : 1,
            WebkitTapHighlightColor: "rgba(0,0,0,0)",
            WebkitAppearance: "none",
          }}
        >
          확인하고 제출하기
        </button>
      </div>

      {/* 업로드 진행 오버레이 */}
      {isUploading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "12px",
              width: "80%",
              maxWidth: "300px",
            }}
          >
            <div style={{ marginBottom: "15px", textAlign: "center" }}>
              제출 중... {uploadProgress}%
            </div>
            <div
              style={{
                width: "100%",
                height: "4px",
                backgroundColor: "#e0e0e0",
                borderRadius: "2px",
              }}
            >
              <div
                style={{
                  width: `${uploadProgress}%`,
                  height: "100%",
                  backgroundColor: "#7A4FFF",
                  borderRadius: "2px",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showErrorModal && (
        <motion.div
          className="error-modal"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
        >
          <p>{uploadError}</p>
        </motion.div>
      )}
    </motion.div>
  );
}

export default SubmissionConfirmPage;
